/** The search syntax in a nutshell, shown under the search box on request (see the Vault's QueryParser). */
export interface SearchTip { example: string; text: string }
export interface SearchTipGroup { title: string; tips: SearchTip[] }

/** A real document of the tenant: what the examples are made of. */
export interface TipSample { fields: [string, string][]; meta: Record<string, string>; otherId?: string }

const ID_VALUE = /^[A-Za-z]{1,6}[-_/]\d{3,}$/;
const wordsOf = (v: string) => v.match(/\p{L}{3,}/gu) ?? [];
const leaf = (path: string) => path.split('/').filter(Boolean).pop() ?? path;

/** Picks the most useful document: one with an identifier and a text value. */
export function pickSample(samples: TipSample[]): TipSample | undefined {
  const score = (s: TipSample) => (s.fields.some(([, v]) => ID_VALUE.test(v)) ? 2 : 0) + (s.fields.some(([, v]) => wordsOf(v).length >= 2 && !ID_VALUE.test(v)) ? 2 : 0) + (s.meta.status ? 1 : 0);
  return [...samples].sort((a, b) => score(b) - score(a))[0];
}

/** The tips with examples from a real document, so that they find something; generic ones without. */
export function buildTips(sample: TipSample | undefined): SearchTipGroup[] {
  const plain = (sample?.fields ?? []).filter(([path]) => !leaf(path).startsWith('@'));
  const idField = plain.find(([, v]) => ID_VALUE.test(v));
  const textField = plain.find(([, v]) => !ID_VALUE.test(v) && wordsOf(v).length >= 2) ?? plain.find(([, v]) => !ID_VALUE.test(v) && wordsOf(v).length >= 1);
  const numField = plain.find(([, v]) => /^\d{1,9}([.,]\d+)?$/.test(v) && Number(v.replace(',', '.')) > 0);
  const id = idField?.[1] ?? 'SO-740110';
  const textWords = textField ? wordsOf(textField[1]) : [];
  const word = textWords[0] ?? 'espoo';
  const second = textWords[1] ?? 'kauppakatu';
  const prefix = word.length > 4 ? word.slice(0, 4) : word.slice(0, Math.max(2, word.length - 1));
  const textLeaf = textField ? leaf(textField[0]) : 'CustName';
  const idLeaf = idField ? leaf(idField[0]) : 'CustOrderNumber';
  const idHead = id.slice(0, Math.max(3, id.length - 2));
  const status = sample?.meta.status || 'FAILED';
  const processWord = (sample?.meta.process ? wordsOf(sample.meta.process)[0] : undefined)?.toLowerCase() ?? 'webshop';
  const other = sample?.otherId && sample.otherId !== id ? sample.otherId : undefined;
  return [
    {
      title: 'Words and values',
      tips: [
        { example: word, text: 'a word anywhere in a document' },
        { example: `${prefix}*`, text: 'words that begin with it (a star works at the end of a word only)' },
        { example: `${word} ${second}`, text: 'both words, anywhere in the document' },
        { example: '"10:30"', text: 'quotes are needed only for text with “:” “=” “<” “>” or a leading “-”; they do not make a phrase' },
        { example: id, text: 'an identifier with separators: the whole value of any field' },
        { example: `${word} -${second}`, text: 'leave out documents with a word' },
        { example: other ? `${id} OR ${other}` : `${id} OR ${word}`, text: 'either one (OR in capitals); group with ( )' },
      ],
    },
    {
      title: 'In a field',
      tips: [
        { example: `${textLeaf}:${word}`, text: 'a word in this field' },
        { example: `${idLeaf}=${id}`, text: 'the exact value of the field' },
        { example: `${idLeaf}=${idHead}*`, text: 'values that begin with it' },
        { example: `${idLeaf}=*`, text: 'the field exists' },
        ...(numField ? [{ example: `${leaf(numField[0])}>=${numField[1]}`, text: 'numbers and dates: > >= < <=, e.g. DeliveryDate>=2026-09-01' }] : [{ example: 'InvoiceTotal>1000', text: 'numbers and dates: > >= < <=, e.g. DeliveryDate>=2026-09-01' }]),
      ],
    },
    {
      title: 'Combine them: every part must match',
      tips: [
        { example: `${word} @status=${status}`, text: 'a word, and the status of the document' },
        { example: `${prefix}* -${second}`, text: 'words that begin with it, leaving out another word' },
        { example: `${textLeaf}:${prefix}* ${idLeaf}=${idHead}*`, text: 'two fields: a word in one and the beginning of a value in the other' },
        { example: `(${id} OR ${other ?? word}) @status=${status}`, text: 'either of two, narrowed by status' },
        { example: numField ? `${word} ${leaf(numField[0])}>=${numField[1]}` : `${word} InvoiceTotal>1000`, text: 'a word and a number or date range' },
        { example: `@process:${processWord} ${word} -@flag=parseError`, text: 'a process, a word, and no parse errors' },
      ],
    },
    {
      title: 'About the document',
      tips: [
        { example: `@status=${status}`, text: 'status, class, runtime, connection, user, executionId, correlationId …' },
        { example: `@process:${processWord}`, text: 'a word in process, connection, operation or info' },
        { example: '@flag=parseError', text: 'data quality: parseError, typeError, truncated, emptyPayload' },
      ],
    },
  ];
}

export const SEARCH_TIPS_NOTE = 'Matching is by whole words or word beginnings, so “katu*” finds Katu and Katuosoite but not Mannerheiminkatu. Put text with “:” “=” “<” “>” or a leading “-” in quotes to search for it as written.';
